import {
  Box,
  SubTitle,
  formattedCurrency,
  Toast,
  GetColorStatus,
  capitalizeFirstLetterOfWords,
  Card,
  ScheduledPayment,
  ScheduledPaymentUpcomingTransaction,
  ScheduledPaymentCompletedTransaction,
  TableProps,
  removeWhiteSpaceURI,
} from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import moment from 'moment';
import { Table } from 'UI';
import TransactionSearchForm from './TransactionSearchForm';
import { useClientData } from '@/providers/ClientData.provider';
import usePostEditScheduledFund from '@/hooks/useApi/usePostEditScheduledFund';
import usePostEditScheduledWithdraw from '@/hooks/useApi/usePostEditScheduledWithdraw';
import styles from '../../ScheduledTransactions.module.scss';
import { usePermissions } from '@/providers/Permissions.provider';
import CancelUpcomingTransactionDialogue from '../../../Dialogue/UpcomingTransaction/CancelTransactionDialogue';
import CancelUpcomingTransactionContext, {
  CancelUpcomingTransactionCtx,
} from '../../../Dialogue/UpcomingTransaction/CancelTransactionContext';
import {
  CancelUpcomingTransactionDialogueHandler,
  EditAmountDialogueHandler,
  DialogueHandlerProps,
} from '../../../Dialogue/types';

import EditAmountDialogue from '../../../Dialogue/EditAmount/EditAmountDialogue';
import EditAmountContext, { EditAmountCtx } from '../../../Dialogue/EditAmount/EditAmountContext';
import EditDateDialogue from '../../../Dialogue/EditDate/EditDateDialogue';
import { useTranslation } from 'react-i18next';
import { Button, TransactionStatus, TableFiltersContainer } from 'UI';

type TransactionsHistoryProps = {
  setNewStatus: (e: boolean) => void;
  transaction: ScheduledPayment;
};

type ScheduledPaymentTransaction = ScheduledPaymentUpcomingTransaction | ScheduledPaymentCompletedTransaction;

type FC = FunctionComponent<TransactionsHistoryProps>;

const TransactionsHistory: FC = (props: TransactionsHistoryProps) => {
  const { response } = useClientData();
  const { permissions } = usePermissions();
  const { transaction, setNewStatus } = props;
  const [status, setStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [transactionsList, setTransactionsList] = useState<ScheduledPaymentTransaction[]>([]);
  const [filteredLists, setFilteredLists] = useState<ScheduledPaymentTransaction[]>([]);
  const [startDate, setStartDate] = useState('');
  const [editAmount, setEditAmount] = useState('');

  const [editScheduledFundResponse, postEditScheduledFund] = usePostEditScheduledFund({});
  const [editScheduledWithdrawResponse, postEditScheduledWithdraw] = usePostEditScheduledWithdraw({});
  const [shouldCancelFund, setShouldCancelFund] = useState(false);
  const [shouldCancelWithdraw, setShouldCancelWithdraw] = useState(false);
  const [shouldOpenEditAmountModal, setShouldOpenEditAmountModal] = useState(false);
  const [shouldEditFundAmount, setShouldEditFundAmount] = useState(false);
  const [shouldEditWithdrawAmount, setShouldEditWithdrawAmount] = useState(false);
  const [showEditAmountErrorAlert, setShowEditAmountErrorAlert] = useState(false);
  const [sendEditAmountError, setSendEditAmountError] = useState('');

  const [shouldOpenEditDateModal, setShouldOpenEditDateModal] = useState(false);
  const [showCancelUpcomingTransactionErrorAlert, setShowCancelUpcomingTransactionErrorAlert] = useState(false);
  const [sendCancelUpcomingTransactionError, setSendCancelUpcomingTransactionError] = useState('');
  const [showSuccessScheduleDateUpdate, setShowSuccessScheduleDateUpdated] = useState(false);
  const [showEditDateToaster, setShowEditDateToaster] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'ScheduledTransactions.Details.History' });
  const { t: tDetails } = useTranslation('translations', { keyPrefix: 'ScheduledTransactions.Details' });

  const scheduleType = transaction.PaymentType === 'debit' ? 'fund' : 'withdraw';
  const isEditableRail = !transaction.TransactionType?.toLowerCase().includes('credit');

  const [newDate, setNewDate] = useState('');
  const CancelUpcomingTransaction: CancelUpcomingTransactionCtx = {
    showCancelUpcomingTransactionErrorAlert,
    sendCancelUpcomingTransactionError,
  };

  const EditAmount: EditAmountCtx = {
    shouldOpenEditAmountModal,
    showEditAmountErrorAlert,
    sendEditAmountError,
  };

  const handleStatusChange = (statusVal: string) => {
    setStatus(statusVal);
  };

  const handleSearchTermChange = (searchTermVal: string) => {
    setSearchTerm(searchTermVal);
  };

  const handlers = {
    handleCancelTransactionDialogue: (handlerProps: DialogueHandlerProps<CancelUpcomingTransactionDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: CancelUpcomingTransactionDialogueHandler = {
        error: {
          success: {
            default: () => {},
          },
          fail: {
            default: () => {
              setShowCancelUpcomingTransactionErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
    handleEditAmountDialogue: (handlerProps: DialogueHandlerProps<EditAmountDialogueHandler>) => {
      const { type, state, action = 'default' } = handlerProps;

      const dialogueHandlers: EditAmountDialogueHandler = {
        confirm: {
          success: {
            default: () => {
              if (editAmount !== '' && Number(editAmount) > 0) {
                setShouldOpenEditAmountModal(false);
                if (scheduleType === 'fund') {
                  setShouldEditFundAmount(true);
                }
                if (scheduleType === 'withdraw') {
                  setShouldEditWithdrawAmount(true);
                }
              }
            },
          },
          fail: {
            default: () => {
              setShouldOpenEditAmountModal(false);
            },
          },
        },
        error: {
          success: {
            default: () => {
              setShowEditAmountErrorAlert(false);
            },
          },
          fail: {
            default: () => {
              setShowEditAmountErrorAlert(false);
            },
          },
        },
      };
      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();
    },
  };

  // create lists
  useEffect(() => {
    if (!transaction) return;

    const merged: ScheduledPaymentTransaction[] = [
      ...transaction.UpcomingTransactions,
      ...transaction.CompletedTransactions,
    ].sort((a, b) => (a.ScheduledDate < b.ScheduledDate ? -1 : 1));

    setTransactionsList(merged);
  }, [transaction]);
  const search = () => {
    const keys: (keyof ScheduledPaymentTransaction)[] = ['Amount', 'ID'];
    setFilteredLists(
      transactionsList.filter(
        (data) =>
          (status === '' || status === 'all' || data.Status === status) &&
          (searchTerm === '' || keys.some((key) => data[key]?.toString().toLowerCase().includes(searchTerm))),
      ),
    );
    setFiltersApplied(!!status || !!searchTerm);
  };
  // search form
  useEffect(() => {
    search();
  }, [transactionsList]);

  useEffect(() => {}, [status, searchTerm]);

  // Edit upcoming transaction
  useEffect(() => {
    if (shouldEditFundAmount) {
      postEditScheduledFund({
        ScheduledTransactionID: transaction?.ID || '',
        UpcomingPayment: startDate,
        Amount: editAmount,
        NewDate: newDate,
      });
    }
    if (shouldEditWithdrawAmount) {
      postEditScheduledWithdraw({
        ScheduledTransactionID: transaction?.ID || '',
        UpcomingPayment: startDate,
        Amount: editAmount,
        NewDate: newDate,
      });
    }
  }, [shouldEditFundAmount, shouldEditWithdrawAmount]);

  useEffect(() => {
    if (shouldEditFundAmount) {
      const { Success, ErrorMessage } = editScheduledFundResponse;

      if (Success === true) {
        setNewStatus(true);

        if (showEditDateToaster) {
          setShowSuccessScheduleDateUpdated(true);
        }
      } else {
        setShowEditAmountErrorAlert(true);
        setSendEditAmountError(ErrorMessage);
      }
    }
    if (shouldEditWithdrawAmount) {
      const { Success, ErrorMessage } = editScheduledWithdrawResponse;

      if (Success === true) {
        setNewStatus(true);

        if (showEditDateToaster) {
          setShowSuccessScheduleDateUpdated(true);
        }
      } else {
        setShowEditAmountErrorAlert(true);
        setSendEditAmountError(ErrorMessage);
      }
    }
    setShouldEditFundAmount(false);
    setShouldEditWithdrawAmount(false);
  }, [editScheduledFundResponse, editScheduledWithdrawResponse]);

  // Cancel upcoming transaction
  useEffect(() => {
    if (shouldCancelFund) {
      postEditScheduledFund({
        ScheduledTransactionID: transaction?.ID || '',
        UpcomingPayment: startDate,
        Amount: '0',
      });
    }
    if (shouldCancelWithdraw) {
      postEditScheduledWithdraw({
        ScheduledTransactionID: transaction?.ID || '',
        UpcomingPayment: startDate,
        Amount: '0',
      });
    }
  }, [shouldCancelFund, shouldCancelWithdraw]);

  useEffect(() => {
    if (shouldCancelFund) {
      const { Success, ErrorMessage } = editScheduledFundResponse;

      if (Success === true) {
        setNewStatus(true);
      } else {
        setShowCancelUpcomingTransactionErrorAlert(true);
        setSendCancelUpcomingTransactionError(ErrorMessage);
      }
    }
    if (shouldCancelWithdraw) {
      const { Success, ErrorMessage } = editScheduledWithdrawResponse;

      if (Success === true) {
        setNewStatus(true);
      } else {
        setShowCancelUpcomingTransactionErrorAlert(true);
        setSendCancelUpcomingTransactionError(ErrorMessage);
      }
    }
    setShouldCancelFund(false);
    setShouldCancelWithdraw(false);
  }, [editScheduledFundResponse, editScheduledWithdrawResponse]);

  useEffect(() => {
    if (showSuccessScheduleDateUpdate) {
      setShowSuccessScheduleDateUpdated(false);
      setShowEditDateToaster(false);
    }
  }, [showSuccessScheduleDateUpdate]);

  const tableProps: TableProps<ScheduledPaymentTransaction> = {
    id: 'transactionHistoryTable',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'scheduledTransactionID',
        header: t('Table.scheduledTransactionID'),
        align: 'left',
        field: 'ID',
      },
      {
        id: 'transactionDate',
        header: t('Table.transactionDate'),
        align: 'left',
        field: 'ScheduledDate',
      },
      {
        id: 'transactionId',
        header: t('Table.transactionId'),
        align: 'left',
        template: ({ data }) => {
          return (
            <Table.Link
              to={`/transactions-history/details/${removeWhiteSpaceURI(scheduleType)}/${data.TransactionID}/${
                transaction.Currency
              }`}
            >
              {data.TransactionID}
            </Table.Link>
          );
        },
      },
      {
        id: 'transactionType',
        header: t('Table.transactionType'),
        align: 'left',
        template: () =>
          tDetails(`ScheduleDetailsSection.Rows.Type.Options.${scheduleType}`, {
            defaultValue: capitalizeFirstLetterOfWords(scheduleType),
          }),
      },
      {
        id: 'amount',
        header: t('Table.amount'),
        align: 'left',
        template: ({ data }) => `$ ${formattedCurrency(data.Amount)} ${response.Country === 'CA' ? 'CAD' : 'USD'} `,
      },
      {
        id: 'status',
        header: t('Table.status'),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus
              status={GetColorStatus(data.Status || '')}
              label={capitalizeFirstLetterOfWords(t(`SearchForm.Status.Options.${data.Status}`, { defaultValue: '' }))}
            />
          </Box>
        ),
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box gap="sm" jc="fe">
            {'ScheduledDateTime' in data &&
              response.Country === 'CA' &&
              isEditableRail &&
              ((scheduleType === 'fund' && permissions.CreateCollections) ||
                (scheduleType === 'withdraw' && permissions.CreatePayments)) && (
                <>
                  <Button
                    id={`edit-date-${data.ID}`}
                    size="small"
                    onClick={() => {
                      setEditAmount(data.Amount);
                      setShouldOpenEditDateModal(true);
                      setStartDate(data.ScheduledDate);
                    }}
                  >
                    {t('Table.Rows.editDateButton')}
                  </Button>
                  <Button
                    id={`edit-amount-${data.ID}`}
                    size="small"
                    onClick={() => {
                      setEditAmount(data.Amount);
                      setShouldOpenEditAmountModal(true);
                      setStartDate(data.ScheduledDate);
                    }}
                  >
                    {t('Table.Rows.editAmountButton')}
                  </Button>
                  {permissions.ManageTransactions && (
                    <Button
                      id={`cancel-${data.ID}`}
                      size="small"
                      onClick={() => {
                        if (scheduleType === 'fund') {
                          setShouldCancelFund(true);
                        }
                        if (scheduleType === 'withdraw') {
                          setShouldCancelWithdraw(true);
                        }
                        setStartDate(data.ScheduledDate);
                      }}
                    >
                      {t('Table.Rows.cancelButton')}
                    </Button>
                  )}
                </>
              )}
          </Box>
        ),
      },
    ],
    rows: filteredLists.map((transaction, index) => ({
      id: String(transaction.ID || index),
      data: transaction,
    })),
  };

  return (
    <CancelUpcomingTransactionContext.Provider value={CancelUpcomingTransaction}>
      <EditAmountContext.Provider value={EditAmount}>
        <Box flx="col" gapSize="36px">
          <SubTitle subTitle={t('title')} />
          <Card
            style={{
              padding: '16px',
            }}
          >
            <Box flx="col" gap="sm">
              <TableFiltersContainer filtersApplied={filtersApplied}>
                <TableFiltersContainer.Trigger />
                <TableFiltersContainer.Content>
                  <TransactionSearchForm
                    status={status}
                    searchTerm={searchTerm}
                    handleStatusChange={handleStatusChange}
                    handleSearchTermChange={handleSearchTermChange}
                    search={search}
                  />
                </TableFiltersContainer.Content>
              </TableFiltersContainer>
            </Box>
            <Box>
              <div className={styles.tableContainer}>
                <Table {...tableProps} />
              </div>
            </Box>
          </Card>
        </Box>

        <div className="hiddenFormsPrint">
          <EditAmountDialogue
            setEditAmount={setEditAmount}
            handleDialogue={handlers.handleEditAmountDialogue}
            initalAmount={editAmount}
          />

          <EditDateDialogue
            visible={shouldOpenEditDateModal}
            onChange={(newDateVal) => {
              setShouldOpenEditDateModal(false);
              if (scheduleType === 'fund') {
                setShouldEditFundAmount(true);
              }
              if (scheduleType === 'withdraw') {
                setShouldEditWithdrawAmount(true);
              }
              setShowEditDateToaster(true);
              setNewDate(newDateVal);
            }}
            initalDate={moment(startDate).valueOf().toString()}
            onClose={() => {
              setShouldOpenEditDateModal(false);
            }}
          />

          <CancelUpcomingTransactionDialogue handleDialogue={handlers.handleCancelTransactionDialogue} />
        </div>
        <Toast label={t('scheduleDateUpdatedToast')} isVisible={showSuccessScheduleDateUpdate} />
      </EditAmountContext.Provider>
    </CancelUpcomingTransactionContext.Provider>
  );
};

export default TransactionsHistory;
